import { useQuery } from "@tanstack/react-query";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import {
  CollapsibleState,
  isCollapsibleExpanded,
} from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { JobLabel } from "~/components/build/Show/routes/list/-components/TreeNode/JobLabel";
import {
  StepLink,
  StepLinkHighlight,
} from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import { CollapseChevron } from "~/components/build/Show/routes/list/-components/TreeNode/CollapseChevron";
import { retryHistoryChildNodes } from "~/components/build/Show/routes/list/-components/TreeNode/retryHistoryNodes";
import { isJobFailed } from "~/components/build/Show/lib/useFilterStore";
import { isJobRunning } from "~/components/build/Show/lib/filterConstants";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { CommandJob } from "~/components/build/Show/lib/types";
import { isJobIgnored } from "~/components/build/Show/lib/isJobIgnored";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { CommandStep, State } from "~/lib/pipeline";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";
import { jobRetriesQueryOptions } from "~/components/build/Show/lib/queries/jobRetries";
import { usePrefetchHandlers } from "~/components/build/Show/lib/usePrefetchHandlers";

interface CommandJobProps {
  step: CommandStep;
  job: CommandJob;
  depth: number;
  collapsibleState?: CollapsibleState;
  /** Whether this row has past attempts to expand into. */
  hasRetryHistory?: boolean;
}

export function CommandJobItem({
  step,
  job,
  depth,
  collapsibleState = CollapsibleState.None,
  hasRetryHistory = false,
}: CommandJobProps) {
  const { build } = useBuild();
  const params = useQueryParams();
  const { currentLatestJobUuid } = useCurrentJobOrStep();

  const isExpanded = isCollapsibleExpanded(collapsibleState);

  const { onMouseEnter, onMouseLeave } = usePrefetchHandlers({
    queries: [jobQueryOptions(build, job.id)],
  });

  // Fetch the retry chain when the history is expanded. The endpoint returns the
  // full chain, which the query writes back into the store so the child rows can
  // render. Gated on expansion so collapsed rows never fetch.
  useQuery(jobRetriesQueryOptions(build, job.id, { enabled: hasRetryHistory && isExpanded }));

  let highlight: StepLinkHighlight | undefined;
  if (isJobFailed(job, build)) {
    highlight = "failed";
  } else if (isJobRunning(job)) {
    highlight = "running";
  }

  return (
    <StepLink
      state={isJobIgnored(job) ? State.Ignored : step.state}
      type={step.type}
      to={resolveJobUrl(job.id, { open: !isJobIgnored(job) })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      active={
        job.id === params.jid ||
        // When collapsed, a selected past attempt highlights this (latest) row
        // so the chain reads as selected. When expanded the attempt's own row
        // is visible and owns the highlight, so don't double up here.
        (!isExpanded && job.id === currentLatestJobUuid && Boolean(params.jid))
      }
      depth={depth}
      highlight={highlight}
      title={isJobIgnored(job) ? job.tooltip || "Skipped jobs cannot be viewed" : undefined}
    >
      <JobLabel
        job={job}
        step={step}
        depth={depth}
        chevron={
          hasRetryHistory ? (
            <CollapseChevron
              nodeId={job.id}
              collapsibleState={collapsibleState}
              data-testid="retry-history-toggle"
              aria-label={isExpanded ? "Hide retry history" : "Show retry history"}
            />
          ) : undefined
        }
      />
    </StepLink>
  );
}

export const createCommandJobNode = (
  step: CommandStep,
  job: CommandJob,
  hasRetryHistory = false,
): TreeNode => ({
  id: job.id,
  height: STEP_ITEM_HEIGHT,
  // Pin the latest row while its expanded retry history scrolls beneath it.
  sticky: hasRetryHistory,
  render: ({ depth, collapsibleState }) => (
    <CommandJobItem
      step={step}
      job={job}
      depth={depth}
      collapsibleState={collapsibleState}
      hasRetryHistory={hasRetryHistory}
    />
  ),
  ...(hasRetryHistory
    ? {
        childNodes: (collapsibleState: CollapsibleState) =>
          retryHistoryChildNodes(step, job, collapsibleState),
      }
    : {}),
});
